"use client";

import * as React from "react";
import NextLink from "next/link";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatPaise } from "@/lib/site-config";
import type { StorefrontPlan } from "@/lib/api";

const FEATURE_LABEL_OVERRIDES: Record<string, string> = {
  crm: "CRM & conversion funnel",
  ai_scribe: "AI ambient scribe",
  ai_voice_receptionist: "AI voice receptionist",
  ai_clinical_assistant: "AI clinical assistant",
  ai_prescription_safety: "AI prescription safety check",
  ai_billing_optimizer: "AI billing optimizer",
  ai_reputation_manager: "AI reputation manager",
  ai_campaign_copywriter: "AI campaign copywriter",
  emr: "EMR",
  hr: "HR / staff lite",
  crm_conversion_funnel: "CRM & conversion funnel",
};

// Plan.included_features stores feature *keys* (e.g. "shared_inbox"), not
// display copy -- humanize for public-facing pricing cards.
function humanizeFeatureKey(key: string): string {
  return FEATURE_LABEL_OVERRIDES[key] ?? key.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

function PricingSection({ plans }: { plans: StorefrontPlan[] }) {
  const [cycle, setCycle] = React.useState<"monthly" | "yearly">("monthly");

  return (
    <div>
      <div className="flex justify-center">
        <div className="inline-flex items-center rounded-full border border-border bg-card p-1">
          <button
            type="button"
            onClick={() => setCycle("monthly")}
            className={cn(
              "cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
              cycle === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground",
            )}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setCycle("yearly")}
            className={cn(
              "cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
              cycle === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground",
            )}
          >
            Yearly
            <span className="ml-1.5 text-xs opacity-80">save ~17%</span>
          </button>
        </div>
      </div>

      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {plans.map((plan) => {
          const price = cycle === "monthly" ? plan.price_monthly : plan.price_yearly;
          return (
            <Card key={plan.slug} className={cn(plan.highlighted && "border-primary shadow-md")}>
              <CardHeader>
                {plan.highlighted && (
                  <Badge variant="info" className="mb-2 w-fit">
                    Most popular
                  </Badge>
                )}
                <CardTitle>{plan.name}</CardTitle>
                {plan.tagline && <CardDescription>{plan.tagline}</CardDescription>}
              </CardHeader>
              <CardContent className="space-y-4">
                {plan.show_price ? (
                  <p className="text-3xl font-semibold text-foreground">
                    {formatPaise(price)}
                    <span className="text-sm font-normal text-muted-foreground">/{cycle === "monthly" ? "mo" : "yr"}</span>
                  </p>
                ) : (
                  <p className="text-3xl font-semibold text-foreground">
                    Custom pricing
                    <span className="mt-1 block text-sm font-normal text-muted-foreground">Priced around your clinic&apos;s size</span>
                  </p>
                )}
                <ul className="space-y-2">
                  {(plan.included_features ?? []).map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                      {humanizeFeatureKey(item)}
                    </li>
                  ))}
                </ul>
                <Button variant={plan.highlighted ? "primary" : "outline"} className="w-full" asChild>
                  {plan.show_price ? (
                    <NextLink href={`/signup?plan=${plan.slug}`}>Start free trial</NextLink>
                  ) : (
                    <NextLink href="/#demo">Contact us</NextLink>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export { PricingSection };
